import { useEffect, useState } from 'react';
import { Heading } from 'react-aria-components';
import { useParams, useNavigate } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getLineDailyAttendanceRecords } from '@/api/getLineDailyAttendanceRecords';
import Button from '@/components/Button/Button';
import {
  AllergyWarningIcon,
  CertificateExpiringIcon,
  CertificateExpiredIcon,
} from '@/components/Icons';
import { LineUserAtom } from '@/jotai/jotai';
import { formattedDate } from '@/logics/date/date';
import { formatTimeToHHMM } from '@/logics/time/timeFormat';
import { AttendanceRecord } from '@/types/attendanceRecord';

export const StaffAttendanceRecordsPage = () => {
  const [lineUser, _] = useAtom(LineUserAtom);
  const [displayDate, setDisplayDate] = useState<string>();
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const { office_id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const today = new Date();
    setDisplayDate(formattedDate(today, 'yyyy年M月dd日(EEE)'));
    if (office_id && lineUser) {
      fetchAttendanceRecords(today);
    }
  }, [lineUser, office_id]);

  const fetchAttendanceRecords = async (date: Date) => {
    const dateString = formattedDate(date, 'yyyy-MM-dd');
    try {
      const result = await getLineDailyAttendanceRecords(
        lineUser?.accessToken ?? '',
        dateString,
        Number(office_id)
      );
      if (result.status === 200) {
        setAttendanceRecords(result.result ?? []);
      } else {
        console.error('出欠記録の取得に失敗しました');
        setAttendanceRecords([]);
      }
    } catch (error) {
      console.error('出欠記録の取得でエラーが発生しました:', error);
      setAttendanceRecords([]);
    }
  };

  const getStatusBadge = (record: AttendanceRecord) => {
    if (record.attendance_status === 'absent') {
      return <span className="rounded-md bg-gray-200 px-3 py-1 text-sm">欠席</span>;
    }

    if (record.is_pickup) {
      return (
        <span className="rounded-md bg-orange-200 px-3 py-1 text-sm text-white">
          送迎あり：{formatTimeToHHMM(record.pickup_time)}下校
        </span>
      );
    } else {
      return (
        <span className="rounded-md bg-green-200 px-3 py-1 text-sm text-white">
          {record.pickup_companion}：{formatTimeToHHMM(record.arrival_time)}来所
        </span>
      );
    }
  };

  return (
    <div className="m-4 space-y-4">
      <Heading className="text-2xl font-bold">本日の利用者</Heading>

      <div className="rounded-lg bg-blue-100 px-4 py-3">
        <div className="text-lg font-medium">{displayDate}</div>
      </div>

      <div className="space-y-4">
        {attendanceRecords.length === 0 ? (
          <div className="py-8 text-center text-gray-500">本日の利用者はいません</div>
        ) : (
          attendanceRecords.map((record, index) => (
            <div
              key={record.child_id}
              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50"
              onClick={() =>
                navigate(
                  `/staff/offices/${office_id}/attendance-records/${record.reservation_id}`,
                  {
                    state: { record, index },
                  }
                )
              }
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-1 items-center">
                  <div className="text-lg font-medium text-gray-900">{record.child_name}</div>
                  <div className="ml-2 flex items-center space-x-1">
                    {record.has_allergy && (
                      <span className="text-red-500">
                        <AllergyWarningIcon
                          width={20}
                          height={20}
                        />
                      </span>
                    )}
                    {record.certificate_status === 'expiring_soon' && (
                      <span className="text-orange-500">
                        <CertificateExpiringIcon
                          width={20}
                          height={20}
                        />
                      </span>
                    )}
                    {record.certificate_status === 'expired' && (
                      <span className="text-red-600">
                        <CertificateExpiredIcon
                          width={20}
                          height={20}
                        />
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  {getStatusBadge(record)}
                  <span className="rounded-md bg-green-400 px-3 py-1 text-sm text-white">
                    {record.school_name}
                    {record.school_grade}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="fixed bottom-4 left-4 right-4">
        <Button
          className="w-full"
          variant="solid-fill"
          size="lg"
          type="button"
          onClick={() => navigate(`/staff/offices/${office_id}/transport`)}
        >
          送迎予約一覧
        </Button>
      </div>
    </div>
  );
};
